import React, { useState, useEffect, useRef } from 'react';
import { Icon, Button, Dropdown } from 'semantic-ui-react';
import moment from 'moment';
import Loader from '../Loader';
import generateAISuggestions from '../../services/generateAISuggestions';
import fetchCronJobHistory from '../../services/fetchCronJobHistory';
import fetchCronJobLogs from '../../services/fetchCronJobLogs';
import './ErrorConsoleAI.scss';

const ErrorConsoleAI = ({ isOpen, onClose, jobData, selectedYear, selectedMonth, adminId }) => {
    const [suggestions, setSuggestions] = useState(null);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState([]);
    const [logs, setLogs] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [viewMode, setViewMode] = useState('overview'); // 'overview', 'history', 'logs'
    const consoleRef = useRef(null);

    useEffect(() => {
        if (isOpen && consoleRef.current) {
            consoleRef.current.focus();
        }

        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen && viewMode === 'history') {
            setError(null);
            loadHistory();
        }
    }, [isOpen, viewMode, selectedYear, selectedMonth, selectedDay]);

    useEffect(() => {
        if (isOpen && viewMode === 'logs') {
            setError(null);
            loadLogs();
        }
    }, [isOpen, viewMode, selectedYear, selectedMonth, selectedDay]);

    const handleGenerateSuggestions = async () => {
        try {
            setIsLoadingSuggestions(true);
            setError(null);

            const payload = {
                jobName: jobData.jobName,
                lastError: (jobData.state && jobData.state.lastFailureError) || 'Unknown error',
                lastFailureAt: jobData.state && jobData.state.lastFailureAt,
                lastSuccessAt: jobData.state && jobData.state.lastSuccessAt,
                consecutiveFailures: (jobData.metrics && jobData.metrics.consecutiveFailures) || 0,
                totalRuns: (jobData.metrics && jobData.metrics.totalRuns) || 0,
                successRate: (jobData.metrics && jobData.metrics.successRate) || 0
            };

            const response = await generateAISuggestions(payload, adminId);
            
            if (response.data && response.data.data && response.data.data.suggestions) {
                setSuggestions(response.data.data.suggestions);
            } else if (response.data && response.data.suggestions) {
                setSuggestions(response.data.suggestions);
            } else {
                setError('No suggestions received from AI');
            }
        } catch (err) {
            console.error('Error generating AI suggestions:', err);
            setError((err.response && err.response.data && err.response.data.message) || 'Failed to generate suggestions. Please try again.');
        } finally {
            setIsLoadingSuggestions(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Never';
        return moment(date).format('DD MMM YYYY, HH:mm:ss');
    };

    const formatRelativeTime = (date) => {
        if (!date) return '';
        return moment(date).fromNow();
    };

    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
        return `${(ms / 60000).toFixed(2)}m`;
    };

    const stripAnsiCodes = (str) => {
        if (!str) return '';
        // Remove ANSI escape codes (color codes like [32m, [39m, etc.)
        return str.replace(/\x1b\[[0-9;]*m/g, '').replace(/\[([0-9]{1,2}m)/g, '');
    };

    const parseAISuggestions = (text) => {
        if (!text) return null;

        // Split by lines and process
        const lines = text.split('\n');
        const elements = [];
        let currentList = [];

        lines.forEach((line, index) => {
            const trimmed = line.trim();

            // Handle empty lines
            if (!trimmed) {
                if (currentList.length > 0) {
                    elements.push(
                        <ul key={`list-${index}`} className="ai-list">
                            {currentList.map((item, idx) => (
                                <li key={idx}>{parseLineContent(item)}</li>
                            ))}
                        </ul>
                    );
                    currentList = [];
                }
                elements.push(<div key={`empty-${index}`} className="ai-spacer" />);
                return;
            }

            // Handle list items (lines starting with * or -)
            if (trimmed.startsWith('*') || trimmed.startsWith('-')) {
                const content = trimmed.replace(/^[\*\-]\s+/, '');
                currentList.push(content);
            } else {
                // Flush any pending list
                if (currentList.length > 0) {
                    elements.push(
                        <ul key={`list-${index}`} className="ai-list">
                            {currentList.map((item, idx) => (
                                <li key={idx}>{parseLineContent(item)}</li>
                            ))}
                        </ul>
                    );
                    currentList = [];
                }

                // Handle headings (lines starting with #)
                if (trimmed.startsWith('##')) {
                    const heading = trimmed.replace(/^#+\s+/, '');
                    elements.push(
                        <h3 key={`h3-${index}`} className="ai-heading-3">
                            {parseLineContent(heading)}
                        </h3>
                    );
                } else if (trimmed.startsWith('#')) {
                    const heading = trimmed.replace(/^#+\s+/, '');
                    elements.push(
                        <h2 key={`h2-${index}`} className="ai-heading-2">
                            {parseLineContent(heading)}
                        </h2>
                    );
                } else {
                    // Regular paragraph
                    elements.push(
                        <p key={`p-${index}`} className="ai-paragraph">
                            {parseLineContent(trimmed)}
                        </p>
                    );
                }
            }
        });

        // Flush any remaining list
        if (currentList.length > 0) {
            elements.push(
                <ul key="final-list" className="ai-list">
                    {currentList.map((item, idx) => (
                        <li key={idx}>{parseLineContent(item)}</li>
                    ))}
                </ul>
            );
        }

        return elements;
    };

    const parseLineContent = (text) => {
        if (!text) return null;

        const parts = [];
        let lastIndex = 0;

        // Regex to match **bold**, *italic*, `code`, and [links]
        const regex = /\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[(.+?)\]\((.+?)\)/g;
        let match;

        while ((match = regex.exec(text)) !== null) {
            // Add text before match
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }

            // Add formatted match
            if (match[1]) {
                // **bold**
                parts.push(
                    <strong key={`bold-${match.index}`} className="ai-bold">
                        {match[1]}
                    </strong>
                );
            } else if (match[2]) {
                // *italic*
                parts.push(
                    <em key={`italic-${match.index}`} className="ai-italic">
                        {match[2]}
                    </em>
                );
            } else if (match[3]) {
                // `code`
                parts.push(
                    <code key={`code-${match.index}`} className="ai-code">
                        {match[3]}
                    </code>
                );
            } else if (match[4] && match[5]) {
                // [link](url)
                parts.push(
                    <a
                        key={`link-${match.index}`}
                        href={match[5]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ai-link"
                    >
                        {match[4]}
                    </a>
                );
            }

            lastIndex = regex.lastIndex;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    const loadHistory = async () => {
        try {
            setIsLoadingHistory(true);
            const params = {
                year: selectedYear,
                month: selectedMonth
            };
            if (selectedDay) {
                params.day = selectedDay;
            }
            const response = await fetchCronJobHistory(jobData.adminId, jobData.jobName, params);
            if (response.data && response.data.data) {
                setHistory(response.data.data.history || []);
            }
        } catch (err) {
            console.error('Error loading history:', err);
            setError('Failed to load history data');
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const loadLogs = async () => {
        try {
            setIsLoadingLogs(true);
            const params = {
                year: selectedYear,
                month: selectedMonth
            };
            if (selectedDay) {
                params.day = selectedDay;
            }
            const response = await fetchCronJobLogs(jobData.adminId, jobData.jobName, params);
            if (response.data && response.data.data) {
                const logsData = response.data.data.logs;
                
                // If logs is a string, store it as a single item
                if (typeof logsData === 'string') {
                    setLogs(logsData ? [{ content: logsData }] : []);
                } else if (Array.isArray(logsData)) {
                    setLogs(logsData);
                } else {
                    setLogs([]);
                }
            }
        } catch (err) {
            console.error('Error loading logs:', err);
            setError('Failed to load logs data');
        } finally {
            setIsLoadingLogs(false);
        }
    };

    // Generate day options for the selected month
    const getDayOptions = () => {
        const daysInMonth = moment(`${selectedYear}-${selectedMonth}`, 'YYYY-M').daysInMonth();
        const options = [{ key: 'all', text: 'All Days', value: null }];
        for (let i = 1; i <= daysInMonth; i++) {
            options.push({
                key: i,
                text: i.toString(),
                value: i
            });
        }
        return options;
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="error-console-overlay" onClick={onClose} />
            <div 
                className={`error-console-ai ${isOpen ? 'open' : ''}`}
                ref={consoleRef}
                tabIndex={-1}
            >
                <div className="console-header">
                    <div className="header-content">
                        <Icon name={jobData.state && jobData.state.currentStatus === 'success' ? 'info circle' : 'bug'} size="large" />
                        <h2>{jobData.state && jobData.state.currentStatus === 'success' ? 'Job Details' : 'Error Console AI'}</h2>
                    </div>
                    <Icon 
                        name="close" 
                        size="large" 
                        link 
                        onClick={onClose}
                        className="close-icon"
                    />
                </div>

                <div className="console-body">
                    {/* View Mode Tabs */}
                    <div className="view-mode-tabs">
                        <Button.Group fluid>
                            <Button
                                active={viewMode === 'overview'}
                                onClick={() => setViewMode('overview')}
                            >
                                <Icon name="info circle" />
                                Overview
                            </Button>
                            <Button
                                active={viewMode === 'history'}
                                onClick={() => setViewMode('history')}
                            >
                                <Icon name="history" />
                                History
                            </Button>
                            <Button
                                active={viewMode === 'logs'}
                                onClick={() => setViewMode('logs')}
                            >
                                <Icon name="file text" />
                                Logs
                            </Button>
                        </Button.Group>
                    </div>

                    {/* Day Selector (for history and logs) */}
                    {(viewMode === 'history' || viewMode === 'logs') && (
                        <div className="day-selector">
                            <span className="selector-label">Day:</span>
                            <Dropdown
                                selection
                                options={getDayOptions()}
                                value={selectedDay}
                                onChange={(e, { value }) => setSelectedDay(value)}
                                placeholder="Select day"
                            />
                        </div>
                    )}

                    {/* Overview Mode */}
                    {viewMode === 'overview' && (
                        <>
                            {/* Job Information Section */}
                            <div className="info-section">
                        <div className="section-title">
                            <Icon name="briefcase" />
                            Job Information
                        </div>
                        <div className="info-card">
                            <div className="info-item">
                                <span className="label">Job Name:</span>
                                <span className="value">{jobData.jobName}</span>
                            </div>
                            <div className="info-item">
                                <span className="label">Current Status:</span>
                                <span className={`value status-badge ${(jobData.state && jobData.state.currentStatus) === 'success' ? 'success' : 'failure'}`}>
                                    <Icon name={(jobData.state && jobData.state.currentStatus) === 'success' ? 'check circle' : 'times circle'} />
                                    {(jobData.state && jobData.state.currentStatus) || 'Unknown'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Execution Timeline Section */}
                    <div className="info-section">
                        <div className="section-title">
                            <Icon name="clock outline" />
                            Execution Timeline
                        </div>
                        <div className="timeline-grid">
                            <div className="timeline-item success">
                                <div className="timeline-icon">
                                    <Icon name="check circle" />
                                </div>
                                <div className="timeline-content">
                                    <div className="timeline-label">Last Success</div>
                                    <div className="timeline-date">
                                        {formatDate(jobData.state && jobData.state.lastSuccessAt)}
                                    </div>
                                    {jobData.state && jobData.state.lastSuccessAt && (
                                        <div className="timeline-relative">
                                            {formatRelativeTime(jobData.state.lastSuccessAt)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="timeline-item failure">
                                <div className="timeline-icon">
                                    <Icon name="times circle" />
                                </div>
                                <div className="timeline-content">
                                    <div className="timeline-label">Last Failure</div>
                                    <div className="timeline-date">
                                        {formatDate(jobData.state && jobData.state.lastFailureAt)}
                                    </div>
                                    {jobData.state && jobData.state.lastFailureAt && (
                                        <div className="timeline-relative">
                                            {formatRelativeTime(jobData.state.lastFailureAt)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error Details Section */}
                    <div className="info-section">
                        <div className="section-title">
                            <Icon name="exclamation triangle" />
                            Error Details
                        </div>
                        <div className="error-details-card">
                            <div className="error-message">
                                {(jobData.state && jobData.state.lastFailureError) || 'No error message available'}
                            </div>
                            <div className="error-stats">
                                <div className="stat-item">
                                    <Icon name="redo" />
                                    <span className="stat-label">Total Runs:</span>
                                    <span className="stat-value">{(jobData.metrics && jobData.metrics.totalRuns) || 0}</span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="warning sign" />
                                    <span className="stat-label">Consecutive Failures:</span>
                                    <span className="stat-value failure-count">
                                        {(jobData.metrics && jobData.metrics.consecutiveFailures) || 0}
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="chart line" />
                                    <span className="stat-label">Success Rate:</span>
                                    <span className="stat-value">
                                        {(jobData.metrics && jobData.metrics.successRate) ? `${jobData.metrics.successRate}%` : 'N/A'}
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="chart line" />
                                    <span className="stat-label">Total Success last 24 Hrs:</span>
                                    <span className="stat-value">
                                        {(jobData.metrics && jobData.metrics.last24Hours.successes) ? jobData.metrics.last24Hours.successes : 'N/A'}
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <Icon name="chart line" />
                                    <span className="stat-label">Total Success last 7 Days:</span>
                                    <span className="stat-value">
                                        {(jobData.metrics && jobData.metrics.last7Days.successes) ? jobData.metrics.last7Days.successes : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI Suggestions Section */}
                    {suggestions && (
                        <div className="info-section suggestions-section">
                            <div className="section-title">
                                <Icon name="lightbulb outline" />
                                AI Suggestions
                            </div>
                            <div className="suggestions-card">
                                <div className="suggestions-content">
                                    {parseAISuggestions(suggestions)}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && viewMode === 'overview' && (
                        <div className="error-state">
                            <Icon name="warning circle" size="large" />
                            <p>{error}</p>
                        </div>
                    )}
                        </>
                    )}

                    {/* History Mode */}
                    {viewMode === 'history' && (
                        <div className="data-view-section">
                            {isLoadingHistory ? (
                                <div className="loading-state">
                                    <Loader />
                                </div>
                            ) : error ? (
                                <div className="error-state">
                                    <Icon name="warning circle" size="large" />
                                    <p>{error}</p>
                                </div>
                            ) : history.length > 0 ? (
                                <div className="history-list">
                                    {history.map((item, index) => (
                                        <div key={index} className="history-item">
                                            <div className="history-header">
                                                <span className={`status-badge ${item.status === 'success' ? 'success' : 'failure'}`}>
                                                    <Icon name={item.status === 'success' ? 'check circle' : 'times circle'} />
                                                    {item.status}
                                                </span>
                                                <span className="history-time">
                                                    {formatDate(item.timestamp)}
                                                </span>
                                            </div>
                                            {item.duration && (
                                                <div className="history-duration">
                                                    Duration: {formatDuration(item.duration)}
                                                </div>
                                            )}
                                            {item.error && (
                                                <div className="history-error">
                                                    <strong>Error:</strong> {item.error}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <Icon name="inbox" size="huge" />
                                    <p>No history data available for the selected period</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Logs Mode */}
                    {viewMode === 'logs' && (
                        <div className="data-view-section">
                            {isLoadingLogs ? (
                                <div className="loading-state">
                                    <Loader />
                                </div>
                            ) : error ? (
                                <div className="error-state">
                                    <Icon name="warning circle" size="large" />
                                    <p>{error}</p>
                                </div>
                            ) : logs.length > 0 ? (
                                <div className="logs-container">
                                    {logs.map((log, index) => (
                                        <div key={index} className="log-content">
                                            {log.content ? (
                                                <pre className="log-text">{stripAnsiCodes(log.content)}</pre>
                                            ) : (
                                                <div className="log-item">
                                                    {log.timestamp && (
                                                        <div className="log-timestamp">
                                                            {formatDate(log.timestamp)}
                                                        </div>
                                                    )}
                                                    <div className="log-message">
                                                        {log.message || log}
                                                    </div>
                                                    {log.level && (
                                                        <span className={`log-level ${log.level.toLowerCase()}`}>
                                                            {log.level}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <Icon name="inbox" size="huge" />
                                    <p>No logs available for the selected period</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer with AI Button - Only show for failed jobs */}
                {jobData.state && jobData.state.currentStatus !== 'success' && (
                    <div className="console-footer">
                        <Button
                            primary
                            fluid
                            size="large"
                            onClick={handleGenerateSuggestions}
                            disabled={isLoadingSuggestions}
                            className="generate-suggestions-btn"
                        >
                            {isLoadingSuggestions ? (
                                <>
                                    <Icon loading name="spinner" />
                                    Generating AI Suggestions...
                                </>
                            ) : (
                                <>
                                    <Icon name="magic" />
                                    Generate AI Suggestions
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

export default ErrorConsoleAI;
